import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  BookOpen, ChevronRight, ChevronDown, ChevronLeft, Search, ArrowLeft, 
  FileText, Clock, CheckCircle, BookMarked, Loader2,
  Bookmark, BookmarkCheck, Home, FolderOpen,
  Trash2, Circle, CheckCircle2, RotateCcw, Info, Flag
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { useModuleAccess } from '../hooks/useModuleAccess';
import SubscriptionGate from '../components/common/SubscriptionGate';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Flag reason types
const FLAG_REASONS = [
  { value: 'incorrect_info', label: 'Incorrect Information', description: 'Facts or concepts are wrong' },
  { value: 'outdated', label: 'Outdated Content', description: 'Information is no longer current' },
  { value: 'typo_grammar', label: 'Typo/Grammar', description: 'Spelling or grammatical errors' },
  { value: 'missing_content', label: 'Missing Content', description: 'Images not loading, incomplete sections' },
  { value: 'confusing', label: 'Confusing Explanation', description: 'Hard to understand' },
  { value: 'other', label: 'Other', description: 'Custom feedback' }
];

// Inline Reading Component (displays in right panel)
const InlineNoteReader = ({ 
  note, 
  onBack, 
  onBookmark, 
  isBookmarked, 
  onProgressUpdate, 
  relatedQuestions,
  isCompleted,
  onToggleComplete,
  onFlagNote,
  onLoadMoreQuestions,
  loadingMoreQuestions,
  onOpenNote,
  onOpenTopic
}) => {
  const scrollRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [flagDescription, setFlagDescription] = useState('');
  const [submittingFlag, setSubmittingFlag] = useState(false);

  // "Also Read" — admin-curated related notes. Fetched per-note; empty list
  // is normal and simply hides the section.
  const [alsoRead, setAlsoRead] = useState([]);
  useEffect(() => {
    if (!note?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/notes/${note.id}/related`);
        if (!cancelled) setAlsoRead(res.data.items || []);
      } catch {
        if (!cancelled) setAlsoRead([]);
      }
    })();
    return () => { cancelled = true; };
  }, [note?.id]);

  // Inline shortcode resolution. Admin drops the token into any note body
  // via "Copy inline link" (from Notes admin for chapters, or Topics admin
  // for topic aggregations); here we scan the stored HTML, batch-resolve
  // IDs, and rewrite each token to either a live anchor
  // (`<a data-note-link="UUID">Title</a>` or `<a data-topic-link="TID">Name</a>`)
  // or a muted "unavailable" pill. Click delegation on the article container
  // promotes the click into an in-place open — same navigation as the
  // sidebar/left-tree.
  //
  // Topic links are grade-scoped: `[[topic:TID]]` only resolves when the
  // topic's grade matches the current note's grade (backend enforces via
  // the `grade` param on /topics/resolve).
  const [resolvedContent, setResolvedContent] = useState(note?.content || '');
  useEffect(() => {
    if (!note?.content) { setResolvedContent(''); return; }
    const noteTokenRe = /\[\[note:([A-Za-z0-9_-]{6,})\]\]/g;
    const topicTokenRe = /\[\[topic:([A-Za-z0-9_-]{3,})\]\]/g;
    const noteIds = Array.from(new Set(Array.from(note.content.matchAll(noteTokenRe), m => m[1])));
    const topicIds = Array.from(new Set(Array.from(note.content.matchAll(topicTokenRe), m => m[1])));
    if (noteIds.length === 0 && topicIds.length === 0) { setResolvedContent(note.content); return; }
    let cancelled = false;
    (async () => {
      try {
        const [noteRes, topicRes] = await Promise.all([
          noteIds.length ? axios.post(`${API}/notes/resolve`, { ids: noteIds }) : Promise.resolve({ data: { items: {} } }),
          topicIds.length ? axios.post(`${API}/topics/resolve`, { ids: topicIds, grade: note.grade }) : Promise.resolve({ data: { items: {} } }),
        ]);
        const noteItems = noteRes.data.items || {};
        const topicItems = topicRes.data.items || {};
        const escape = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        let rewritten = note.content.replace(noteTokenRe, (_, id) => {
          const meta = noteItems[id];
          if (meta && meta.available) {
            return `<a href="#" data-note-link="${id}" class="inline-note-link">${escape(meta.title || 'Linked note')}</a>`;
          }
          return `<span class="unavailable-note-link" title="This chapter is no longer available">chapter unavailable</span>`;
        });
        rewritten = rewritten.replace(topicTokenRe, (_, id) => {
          const meta = topicItems[id];
          if (meta && meta.available) {
            return `<a href="#" data-topic-link="${id}" class="inline-note-link">${escape(meta.name || 'Linked topic')}</a>`;
          }
          return `<span class="unavailable-note-link" title="This topic is no longer available in this grade">topic unavailable</span>`;
        });
        if (!cancelled) setResolvedContent(rewritten);
      } catch {
        if (!cancelled) setResolvedContent(note.content);
      }
    })();
    return () => { cancelled = true; };
  }, [note?.content, note?.grade]);

  // Delegate clicks on rendered inline links to the parent's openNote / openTopic.
  const handleArticleClick = (e) => {
    const noteAnchor = e.target.closest('a[data-note-link]');
    if (noteAnchor) {
      e.preventDefault();
      const targetId = noteAnchor.getAttribute('data-note-link');
      if (targetId && typeof onOpenNote === 'function') {
        axios.get(`${API}/notes/${targetId}`).then(res => onOpenNote(res.data)).catch(() => {});
      }
      return;
    }
    const topicAnchor = e.target.closest('a[data-topic-link]');
    if (topicAnchor) {
      e.preventDefault();
      const targetId = topicAnchor.getAttribute('data-topic-link');
      if (targetId && typeof onOpenTopic === 'function') {
        onOpenTopic(targetId);
      }
    }
  };
  
  // Track scroll progress within the scroll area
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;
    
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      const progress = Math.min(100, Math.round((scrollTop / (scrollHeight - clientHeight)) * 100));
      setScrollProgress(isNaN(progress) ? 0 : progress);
    };
    
    scrollElement.addEventListener('scroll', handleScroll);
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Update progress periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (scrollProgress > 0) {
        onProgressUpdate?.(scrollProgress);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [scrollProgress, onProgressUpdate]);
  
  const handleSubmitFlag = async () => {
    if (!flagReason) {
      toast.error('Please select a reason');
      return;
    }
    
    setSubmittingFlag(true);
    try {
      await onFlagNote(note.id, flagReason, flagDescription);
      setFlagModalOpen(false);
      setFlagReason('');
      setFlagDescription('');
    } catch (error) {
      // Error handled in parent
    } finally {
      setSubmittingFlag(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Progress bar at top */}
      <div className="h-1 bg-gray-200 flex-shrink-0">
        <div 
          className="h-full bg-[#14B8A6] transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>
      
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-white flex-shrink-0">
        <Button variant="ghost" size="sm" onClick={onBack} className="text-gray-600">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to chapters
        </Button>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setFlagModalOpen(true)}
            className="text-amber-600 border-amber-200 hover:bg-amber-50"
          >
            <Flag className="w-4 h-4 mr-1" /> Report
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={onBookmark}
            className={isBookmarked ? 'text-[#14B8A6] border-[#14B8A6]' : ''}
          >
            {isBookmarked ? (
              <><BookmarkCheck className="w-4 h-4 mr-1" /> Saved</>
            ) : (
              <><Bookmark className="w-4 h-4 mr-1" /> Save</>
            )}
          </Button>
        </div>
      </div>
      
      {/* Flag Report Modal */}
      <Dialog open={flagModalOpen} onOpenChange={setFlagModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-amber-600" />
              Report an Issue
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>What&apos;s wrong with this chapter?</Label>
              <Select value={flagReason} onValueChange={setFlagReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason..." />
                </SelectTrigger>
                <SelectContent>
                  {FLAG_REASONS.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      <div>
                        <div className="font-medium">{reason.label}</div>
                        <div className="text-xs text-gray-500">{reason.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Additional details (optional)</Label>
              <Textarea
                value={flagDescription}
                onChange={(e) => setFlagDescription(e.target.value)}
                placeholder="Describe the issue in more detail..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlagModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitFlag} 
              disabled={submittingFlag || !flagReason}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {submittingFlag ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
              ) : (
                'Submit Report'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Scrollable Content */}
      <ScrollArea className="flex-1" ref={scrollRef}>
        <div className="p-6 max-w-3xl mx-auto">
          {/* Note Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{note.title}</h1>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <Badge variant="outline">{note.grade}</Badge>
              <span>Chapter {note.order}</span>
            </div>
          </div>
          
          {/* Note Content */}
          <article className="prose prose-lg max-w-none mb-8" onClick={handleArticleClick}>
            <div 
              className="
                [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-8 [&_h1]:mb-4
                [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-6 [&_h2]:mb-3
                [&_h3]:text-lg [&_h3]:font-medium [&_h3]:mt-4 [&_h3]:mb-2
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4
                [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4
                [&_li]:mb-2
                [&_blockquote]:border-l-4 [&_blockquote]:border-[#14B8A6] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4
                [&_pre]:bg-gray-900 [&_pre]:text-white [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto
                [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:rounded
                [&_img]:max-w-full [&_img]:rounded-lg [&_img]:my-4
                [&_table]:w-full [&_table]:border-collapse [&_table]:my-4
                [&_th]:border [&_th]:border-gray-300 [&_th]:p-2 [&_th]:bg-gray-100 [&_th]:font-semibold
                [&_td]:border [&_td]:border-gray-300 [&_td]:p-2
                [&_a]:text-[#14B8A6] [&_a]:underline
                [&_.inline-note-link]:cursor-pointer [&_.inline-note-link]:font-medium [&_.inline-note-link]:no-underline [&_.inline-note-link]:bg-sky-50 [&_.inline-note-link]:px-1.5 [&_.inline-note-link]:py-0.5 [&_.inline-note-link]:rounded [&_.inline-note-link]:border [&_.inline-note-link]:border-sky-200 [&_.inline-note-link]:text-sky-700 [&_.inline-note-link]:break-words [&_.inline-note-link]:leading-snug
                [&_.inline-note-link:hover]:bg-sky-100
                [&_.unavailable-note-link]:italic [&_.unavailable-note-link]:text-gray-400 [&_.unavailable-note-link]:bg-gray-50 [&_.unavailable-note-link]:px-1.5 [&_.unavailable-note-link]:py-0.5 [&_.unavailable-note-link]:rounded [&_.unavailable-note-link]:border [&_.unavailable-note-link]:border-gray-200 [&_.unavailable-note-link]:break-words
                [&_iframe]:max-w-full [&_iframe]:my-4 [&_iframe]:mx-auto [&_iframe]:block
              "
              dangerouslySetInnerHTML={{ __html: resolvedContent }}
            />
          </article>

          {/* Also Read — admin-curated related chapters. Grade-scoped,
              cross-function. Order matches what the admin picked. */}
          {alsoRead.length > 0 && (
            <div className="border-t pt-6 mt-6" data-testid="also-read-block">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-sky-600" />
                Also Read
              </h2>
              <div className="grid gap-2 sm:grid-cols-2">
                {alsoRead.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      axios.get(`${API}/notes/${item.id}`).then(res => {
                        if (typeof onOpenNote === 'function') onOpenNote(res.data);
                      });
                    }}
                    className="text-left p-3 border rounded-lg hover:border-sky-300 hover:bg-sky-50/50 transition-colors group"
                    data-testid={`also-read-item-${item.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-sky-500 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 line-clamp-2">{item.title}</p>
                        {item.function_name && (
                          <p className="text-xs text-gray-500 mt-0.5">{item.function_name}</p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Related Questions - Oral and Written Tabs (Simplified) */}
          {(relatedQuestions.oral?.length > 0 || relatedQuestions.written?.length > 0) && (
            <div className="border-t pt-6 mt-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#0A7EA4]" />
                Related Questions
              </h2>
              
              <Tabs defaultValue="oral" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="oral" className="flex items-center gap-2">
                    Oral Questions
                    {relatedQuestions.oral?.length > 0 && (
                      <Badge variant="secondary" className="text-xs">{relatedQuestions.oral.length}</Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="written" className="flex items-center gap-2">
                    Written Questions
                    {relatedQuestions.written?.length > 0 && (
                      <Badge variant="secondary" className="text-xs">{relatedQuestions.written.length}</Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="oral">
                  {relatedQuestions.oral?.length > 0 ? (
                    <div className="space-y-2">
                      {relatedQuestions.oral.map((q, idx) => (
                        <div key={q.id || idx} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                          <p className="text-sm text-gray-800">{q.question_text}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                            {q.mmd_location && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                {q.mmd_location}
                              </Badge>
                            )}
                            {q.surveyor_name && (
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                {q.surveyor_name}
                              </Badge>
                            )}
                            {q.exam_month && (
                              <span className="text-gray-500 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {q.exam_month}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {/* Show Next 10 Button */}
                      {relatedQuestions.oral.length < relatedQuestions.oralTotal && (
                        <div className="text-center pt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onLoadMoreQuestions?.('oral', note)}
                            disabled={loadingMoreQuestions?.oral}
                            className="text-[#0A7EA4] border-[#0A7EA4] hover:bg-[#0A7EA4]/10"
                          >
                            {loadingMoreQuestions?.oral ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Loading...
                              </>
                            ) : (
                              <>
                                Show Next 10
                                <ChevronDown className="w-4 h-4 ml-1" />
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No oral questions for this topic yet</p>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="written">
                  {relatedQuestions.written?.length > 0 ? (
                    <div className="space-y-2">
                      {relatedQuestions.written.map((q, idx) => (
                        <div key={q.id || idx} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                          <p className="text-sm text-gray-800">{q.question_text}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                            {q.marks && (
                              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                {q.marks} marks
                              </Badge>
                            )}
                            {q.paper_name && (
                              <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600">
                                {q.paper_name}
                              </Badge>
                            )}
                            {q.exam_month && (
                              <span className="text-gray-500 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {q.exam_month}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {/* Show Next 10 Button */}
                      {relatedQuestions.written.length < relatedQuestions.writtenTotal && (
                        <div className="text-center pt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onLoadMoreQuestions?.('written', note)}
                            disabled={loadingMoreQuestions?.written}
                            className="text-[#0A7EA4] border-[#0A7EA4] hover:bg-[#0A7EA4]/10"
                          >
                            {loadingMoreQuestions?.written ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Loading...
                              </>
                            ) : (
                              <>
                                Show Next 10
                                <ChevronDown className="w-4 h-4 ml-1" />
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No written questions for this topic yet</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
          
          {/* Completion Toggle */}
          <div className="text-center py-6 border-t mt-6">
            {isCompleted ? (
              <Button
                variant="outline"
                onClick={onToggleComplete}
                className="text-green-600 border-green-300 hover:bg-green-50"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Completed
                <RotateCcw className="w-3 h-3 ml-2 opacity-50" />
              </Button>
            ) : (
              <Button
                onClick={onToggleComplete}
                className="bg-[#14B8A6] hover:bg-[#0F766E]"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark as Complete
              </Button>
            )}
            {isCompleted && (
              <p className="text-xs text-gray-400 mt-2">Click to mark as incomplete</p>
            )}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

// Main Notebook Page with Two-Panel Layout
const NotebookPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { preferences } = usePreferences();
  const { canAccessNotebook, subscribedGrade, loading: accessLoading } = useSubscriptionAccess();
  const { isNotebookActiveForGrade, getNotebookStatusForGrade, isModuleHidden, loading: moduleLoading } = useModuleAccess();
  
  const userGrade = preferences?.grade;
  
  // Get notebook module key for the user's grade
  const notebookModuleKey = userGrade ? `notebook_${userGrade.toLowerCase().replace(/\s+/g, '_')}` : null;
  
  // Check if notebook is active for the user's grade
  const isNotebookAvailable = userGrade ? isNotebookActiveForGrade(userGrade) : false;
  const notebookStatus = userGrade ? getNotebookStatusForGrade(userGrade) : null;
  const isHidden = notebookModuleKey ? isModuleHidden(notebookModuleKey) : false;
  
  // State
  const [loading, setLoading] = useState(true);
  const [structure, setStructure] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Navigation state
  const [expandedFunctions, setExpandedFunctions] = useState({});
  const [expandedTopics, setExpandedTopics] = useState({});
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicNotes, setTopicNotes] = useState([]);
  const [topicChildGroups, setTopicChildGroups] = useState([]);
  const [topicInfo, setTopicInfo] = useState(null);
  
  // Reading state
  const [selectedNote, setSelectedNote] = useState(null);
  const [relatedQuestions, setRelatedQuestions] = useState({ 
    oral: [], 
    written: [],
    oralTotal: 0,
    writtenTotal: 0,
    oralOffset: 0,
    writtenOffset: 0
  });
  const [loadingMoreQuestions, setLoadingMoreQuestions] = useState({ oral: false, written: false });
  
  // User data
  const [myProgress, setMyProgress] = useState({});
  const [myBookmarks, setMyBookmarks] = useState(new Set());
  const [myBookmarksList, setMyBookmarksList] = useState([]);
  const [completedNotes, setCompletedNotes] = useState([]);
  const [allNotesWithProgress, setAllNotesWithProgress] = useState([]); // All notes that user has started/completed
  
  // Sidebar filter - Changed to: all | completed | bookmarks
  const [sidebarFilter, setSidebarFilter] = useState('all');

  // Fetch structure for user's grade
  const fetchStructure = useCallback(async () => {
    if (!userGrade) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/notes/by-grade/${encodeURIComponent(userGrade)}`);
      setStructure(response.data);
      
      // Auto-expand first function with notes
      if (response.data?.functions?.length > 0) {
        const firstWithNotes = response.data.functions.find(f => 
          f.topics?.some(t => t.published_notes_count > 0)
        );
        if (firstWithNotes) {
          setExpandedFunctions({ [firstWithNotes.id]: true });
        }
      }
    } catch (error) {
      console.error('Failed to fetch structure:', error);
      toast.error('Failed to load notebook');
    } finally {
      setLoading(false);
    }
  }, [userGrade]);
  
  // Fetch user's reading progress
  const fetchMyProgress = useCallback(async () => {
    if (!isAuthenticated()) return;
    
    try {
      const response = await axios.get(`${API}/notes/progress/my`, {
        params: { grade: userGrade }
      });
      const progressMap = {};
      response.data.progress.forEach(p => {
        progressMap[p.note_id] = p;
      });
      setMyProgress(progressMap);
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    }
  }, [userGrade, isAuthenticated]);
  
  // Fetch user's bookmarks
  const fetchMyBookmarks = useCallback(async () => {
    if (!isAuthenticated()) return;
    
    try {
      const response = await axios.get(`${API}/notes/bookmarks/my`);
      const bookmarks = response.data.bookmarks || [];
      const bookmarkSet = new Set(bookmarks.map(b => b.note_id));
      setMyBookmarks(bookmarkSet);
      setMyBookmarksList(bookmarks);
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
    }
  }, [isAuthenticated]);
  
  // Fetch completed notes for the Completed filter tab
  const fetchCompletedNotes = useCallback(async () => {
    if (!isAuthenticated()) return;
    
    const completed = Object.values(myProgress)
      .filter(p => p.is_completed)
      .sort((a, b) => new Date(b.last_read_at) - new Date(a.last_read_at));
    
    const notesWithDetails = await Promise.all(
      completed.slice(0, 20).map(async (p) => {
        try {
          const res = await axios.get(`${API}/notes/${p.note_id}`);
          return { ...res.data, progress: p };
        } catch {
          return null;
        }
      })
    );
    
    setCompletedNotes(notesWithDetails.filter(Boolean));
  }, [isAuthenticated, myProgress]);
  
  // Fetch all notes that have progress (for indicator calculation)
  const fetchAllNotesWithProgress = useCallback(async () => {
    if (!isAuthenticated()) return;
    
    const allWithProgress = Object.values(myProgress);
    
    const notesWithDetails = await Promise.all(
      allWithProgress.map(async (p) => {
        try {
          const res = await axios.get(`${API}/notes/${p.note_id}`);
          return { ...res.data, progress: p };
        } catch {
          return null;
        }
      })
    );
    
    setAllNotesWithProgress(notesWithDetails.filter(Boolean));
  }, [isAuthenticated, myProgress]);
  
  // Fetch notes for a topic
  const fetchTopicNotes = useCallback(async (topic, funcTopics) => {
    try {
      // If this topic has child topics, ask the backend for the aggregated
      // view so we can render "parent chapters first, then grouped children"
      // without a fan-out of client-side requests.
      const hasChildren = funcTopics?.some(t => t.parent_topic_id === topic.id);
      const params = hasChildren ? { include_descendants: true } : {};
      const response = await axios.get(`${API}/notes/by-topic/${topic.id}`, { params });
      setTopicNotes(response.data.notes || []);
      setTopicChildGroups(response.data.child_groups || []);
      setTopicInfo({
        topic: response.data.topic,
        function: response.data.function
      });
      setSelectedTopic(topic);
      setSelectedNote(null); // Close any open note
    } catch (error) {
      console.error('Failed to fetch topic notes:', error);
      toast.error('Failed to load notes');
    }
  }, []);
  
  // Fetch related questions (both oral and written)
  const fetchRelatedQuestions = useCallback(async (note, reset = true) => {
    try {
      const oralOffset = reset ? 0 : relatedQuestions.oralOffset;
      const writtenOffset = reset ? 0 : relatedQuestions.writtenOffset;
      
      // Fetch oral questions - use MMD from preferences
      const oralParams = {
        grade: note.grade,
        function_id: note.function_id,
        topic_id: note.topic_id,
        question_type: 'oral',
        per_page: 10,
        page: Math.floor(oralOffset / 10) + 1
      };
      
      // Add MMD filter for oral questions if available in preferences
      if (preferences?.mmd_location) {
        oralParams.mmd_location = preferences.mmd_location;
      }
      
      const oralResponse = await axios.get(`${API}/questions`, { params: oralParams });
      
      // Fetch written questions (no MMD filter needed)
      const writtenResponse = await axios.get(`${API}/questions`, {
        params: {
          grade: note.grade,
          topic_id: note.topic_id,
          question_type: 'written',
          per_page: 10,
          page: Math.floor(writtenOffset / 10) + 1
        }
      });
      
      // API returns array directly
      const oralQuestions = Array.isArray(oralResponse.data) ? oralResponse.data : (oralResponse.data.questions || []);
      const writtenQuestions = Array.isArray(writtenResponse.data) ? writtenResponse.data : (writtenResponse.data.questions || []);
      
      // Estimate totals
      const oralTotal = oralQuestions.length >= 10 ? 100 : oralQuestions.length;
      const writtenTotal = writtenQuestions.length >= 10 ? 100 : writtenQuestions.length;
      
      if (reset) {
        setRelatedQuestions({
          oral: oralQuestions,
          written: writtenQuestions,
          oralTotal,
          writtenTotal,
          oralOffset: oralQuestions.length,
          writtenOffset: writtenQuestions.length
        });
      }
    } catch (error) {
      setRelatedQuestions({ oral: [], written: [], oralTotal: 0, writtenTotal: 0, oralOffset: 0, writtenOffset: 0 });
    }
  }, [preferences?.mmd_location, relatedQuestions.oralOffset, relatedQuestions.writtenOffset]);

  // Load more questions
  const loadMoreQuestions = useCallback(async (type, note) => {
    if (!note) return;
    
    setLoadingMoreQuestions(prev => ({ ...prev, [type]: true }));
    
    try {
      const currentOffset = type === 'oral' ? relatedQuestions.oralOffset : relatedQuestions.writtenOffset;
      const page = Math.floor(currentOffset / 10) + 1;
      
      const params = {
        grade: note.grade,
        topic_id: note.topic_id,
        question_type: type,
        per_page: 10,
        page
      };
      
      if (type === 'oral') {
        params.function_id = note.function_id;
        if (preferences?.mmd_location) {
          params.mmd_location = preferences.mmd_location;
        }
      }
      
      const response = await axios.get(`${API}/questions`, { params });
      const newQuestions = Array.isArray(response.data) ? response.data : (response.data.questions || []);
      
      if (newQuestions.length > 0) {
        setRelatedQuestions(prev => ({
          ...prev,
          [type]: [...prev[type], ...newQuestions],
          [`${type}Offset`]: prev[`${type}Offset`] + newQuestions.length
        }));
      }
    } catch (error) {
      toast.error('Failed to load more questions');
    } finally {
      setLoadingMoreQuestions(prev => ({ ...prev, [type]: false }));
    }
  }, [relatedQuestions.oralOffset, relatedQuestions.writtenOffset, preferences?.mmd_location]);
  
  // Search notes
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSearchResults(null);
      return;
    }
    
    try {
      setSearchLoading(true);
      const response = await axios.get(`${API}/notes/search`, {
        params: { q: searchQuery, grade: userGrade }
      });
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery, userGrade]);
  
  // Update reading progress
  const updateProgress = useCallback(async (noteId, position, markComplete = false) => {
    if (!isAuthenticated()) return;
    
    try {
      await axios.put(`${API}/notes/${noteId}/progress`, {
        last_position: position,
        is_completed: markComplete || position >= 95,
        add_read_time_seconds: 10
      });
      
      if (markComplete) {
        toast.success('Marked as complete!');
        fetchMyProgress();
      }
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
  }, [isAuthenticated, fetchMyProgress]);
  
  // Toggle completion status
  const toggleCompletion = useCallback(async (noteId) => {
    if (!isAuthenticated()) return;
    
    const currentProgress = myProgress[noteId];
    const isCurrentlyCompleted = currentProgress?.is_completed;
    
    try {
      await axios.put(`${API}/notes/${noteId}/progress`, {
        last_position: isCurrentlyCompleted ? (currentProgress?.last_position || 50) : 100,
        is_completed: !isCurrentlyCompleted,
        add_read_time_seconds: 0
      });
      
      toast.success(isCurrentlyCompleted ? 'Marked as incomplete' : 'Marked as complete!');
      fetchMyProgress();
    } catch (error) {
      console.error('Failed to toggle completion:', error);
      toast.error('Failed to update');
    }
  }, [isAuthenticated, myProgress, fetchMyProgress]);
  
  // Toggle bookmark
  const toggleBookmark = useCallback(async (noteId) => {
    if (!isAuthenticated()) {
      toast.error('Please login to bookmark notes');
      return;
    }
    
    try {
      if (myBookmarks.has(noteId)) {
        const bookmark = myBookmarksList.find(b => b.note_id === noteId);
        if (bookmark) {
          await axios.delete(`${API}/notes/bookmarks/${bookmark.id}`);
          setMyBookmarks(prev => {
            const next = new Set(prev);
            next.delete(noteId);
            return next;
          });
          setMyBookmarksList(prev => prev.filter(b => b.note_id !== noteId));
          toast.success('Bookmark removed');
        }
      } else {
        await axios.post(`${API}/notes/bookmarks`, { note_id: noteId });
        setMyBookmarks(prev => new Set([...prev, noteId]));
        fetchMyBookmarks();
        toast.success('Bookmarked!');
      }
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  }, [isAuthenticated, myBookmarks, myBookmarksList, fetchMyBookmarks]);
  
  // Flag/report a note
  const flagNote = useCallback(async (noteId, reasonType, description) => {
    if (!isAuthenticated()) {
      toast.error('Please login to report issues');
      return;
    }
    
    try {
      await axios.post(`${API}/notes/${noteId}/report`, {
        reason_type: reasonType,
        description: description || null
      });
      toast.success('Report submitted. Thank you for your feedback!');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'You have already reported this issue');
      } else {
        toast.error('Failed to submit report');
      }
      throw error;
    }
  }, [isAuthenticated]);
  
  // Open note for reading (inline)
  const openNote = useCallback(async (note) => {
    setSelectedNote(note);
    await fetchRelatedQuestions(note);
  }, [fetchRelatedQuestions]);

  // Open a topic by ID — resolves the topic + its sibling topics under the
  // same function from the already-loaded `structure`, then delegates to
  // `fetchTopicNotes` (which handles the aggregated child-groups view when
  // the topic has children). Used by inline `[[topic:TID]]` links in note bodies.
  const openTopicById = useCallback(async (topicId) => {
    if (!structure?.functions) return;
    let found = null;
    let funcTopics = [];
    for (const func of structure.functions) {
      const t = (func.topics || []).find(x => x.id === topicId);
      if (t) { found = t; funcTopics = func.topics; break; }
    }
    if (!found) {
      // Fallback: fetch a minimal topic shape from the backend and open
      // with an empty siblings list — the by-topic endpoint still handles
      // aggregation correctly via include_descendants.
      try {
        const res = await axios.get(`${API}/topics`, { params: { grade: userGrade } });
        const t = (res.data || []).find(x => x.id === topicId);
        if (t) { found = t; }
      } catch (_) { /* ignore — leave `found` null; caller handles no-op */ }
    }
    if (!found) return;
    setSelectedNote(null);
    await fetchTopicNotes(found, funcTopics);
  }, [structure, userGrade, fetchTopicNotes]);
  
  // Initial load
  useEffect(() => {
    if (userGrade) {
      fetchStructure();
      fetchMyProgress();
      fetchMyBookmarks();
    }
  }, [userGrade, fetchStructure, fetchMyProgress, fetchMyBookmarks]);
  
  // Fetch completed notes when progress changes
  useEffect(() => {
    if (Object.keys(myProgress).length > 0) {
      fetchCompletedNotes();
      fetchAllNotesWithProgress();
    }
  }, [myProgress, fetchCompletedNotes, fetchAllNotesWithProgress]);
  
  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        handleSearch();
      } else {
        setSearchResults(null);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);
  
  // Toggle function expansion
  const toggleFunction = (funcId) => {
    setExpandedFunctions(prev => ({
      ...prev,
      [funcId]: !prev[funcId]
    }));
  };
  
  // Toggle topic expansion
  const toggleTopic = (topicId) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  };
  
  // Get progress indicator for a note
  const getNoteProgress = (noteId) => {
    const progress = myProgress[noteId];
    if (!progress) return null;
    if (progress.is_completed) return 'completed';
    if (progress.last_position > 0) return 'in_progress';
    return null;
  };
  
  // Get topic status indicator - Shows green only when ALL chapters are completed
  const getTopicIndicator = (topic, allTopics) => {
    // Get total notes count for topic and children
    const getTotalNotesCount = (t) => {
      let count = t.published_notes_count || 0;
      const children = allTopics?.filter(child => child.parent_topic_id === t.id) || [];
      children.forEach(child => {
        count += getTotalNotesCount(child);
      });
      return count;
    };
    
    // Get completed notes count for topic and children using allNotesWithProgress array
    const getCompletedNotesCount = (t) => {
      // Count completed notes that belong to this topic
      let count = allNotesWithProgress.filter(note => 
        note.topic_id === t.id && note.progress?.is_completed
      ).length;
      
      // Count from children
      const children = allTopics?.filter(child => child.parent_topic_id === t.id) || [];
      children.forEach(child => {
        count += getCompletedNotesCount(child);
      });
      return count;
    };
    
    // Check if topic or children have in-progress notes
    const hasInProgressNotes = (t) => {
      // Check if any note in allNotesWithProgress has in-progress status for this topic
      const hasOwn = allNotesWithProgress.some(note => 
        note.topic_id === t.id && 
        note.progress && 
        !note.progress.is_completed && 
        note.progress.last_position > 0
      );
      if (hasOwn) return true;
      
      const children = allTopics?.filter(child => child.parent_topic_id === t.id) || [];
      return children.some(child => hasInProgressNotes(child));
    };
    
    // Check if any progress exists (some notes completed but not all)
    const hasSomeCompleted = (t) => {
      const hasOwn = allNotesWithProgress.some(note => 
        note.topic_id === t.id && note.progress?.is_completed
      );
      if (hasOwn) return true;
      
      const children = allTopics?.filter(child => child.parent_topic_id === t.id) || [];
      return children.some(child => hasSomeCompleted(child));
    };
    
    const totalNotes = getTotalNotesCount(topic);
    const completedCount = getCompletedNotesCount(topic);
    
    // No notes in topic hierarchy
    if (totalNotes === 0) {
      return <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />;
    }
    
    // ALL notes completed - green tick
    if (completedCount === totalNotes && totalNotes > 0) {
      return <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />;
    }
    
    // Some progress (in-progress or partially completed) - amber clock
    if (hasInProgressNotes(topic) || hasSomeCompleted(topic)) {
      return <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />;
    }
    
    // Has notes but not started - teal circle
    return <Circle className="w-4 h-4 text-[#14B8A6] flex-shrink-0" />;
  };
  
  // Build topic tree
  const buildTopicTree = (topics) => {
    const topicMap = {};
    const rootTopics = [];
    
    topics.forEach(topic => {
      topicMap[topic.id] = { ...topic, children: [] };
    });
    
    topics.forEach(topic => {
      if (topic.parent_topic_id && topicMap[topic.parent_topic_id]) {
        topicMap[topic.parent_topic_id].children.push(topicMap[topic.id]);
      } else if (!topic.parent_topic_id) {
        rootTopics.push(topicMap[topic.id]);
      }
    });
    
    return rootTopics;
  };
  
  // Check if topic or children have notes
  const topicHasNotes = (topic) => {
    if (topic.published_notes_count > 0) return true;
    if (topic.children && topic.children.length > 0) {
      return topic.children.some(child => topicHasNotes(child));
    }
    return false;
  };

  // Recursive rollup helper (iter 105.3): sum published note counts
  // across a topic and every descendant. Powers the "0 here · N in
  // sub-topics" hint so users know to expand parent nodes.
  const rollupPublishedNotes = (topic) => {
    const own = topic.published_notes_count || 0;
    const kids = (topic.children || []).reduce((s, c) => s + rollupPublishedNotes(c), 0);
    return own + kids;
  };

  // Render topic tree
  const renderTopicTree = (topics, func, allTopics, level = 0) => {
    return topics.map((topic) => {
      if (!topicHasNotes(topic)) return null;
      
      const hasChildren = topic.children && topic.children.filter(c => topicHasNotes(c)).length > 0;
      const isExpanded = expandedTopics[topic.id];
      const isSelected = selectedTopic?.id === topic.id;
      const own = topic.published_notes_count || 0;
      const descendantNotes = rollupPublishedNotes(topic) - own;
      
      return (
        <div key={topic.id}>
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  data-testid={`topic-${topic.id}`}
                  className={`flex items-start gap-2 p-2 pr-3 rounded-lg cursor-pointer transition-colors ${
                    isSelected 
                      ? 'bg-[#14B8A6]/10 text-[#14B8A6]' 
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                  style={{ paddingLeft: `${8 + level * 16}px` }}
                  onClick={() => {
                    if (hasChildren) {
                      toggleTopic(topic.id);
                    }
                    // Iter 105.5 fix: fetch when EITHER the topic has
                    // its own chapters OR any descendant does. Prior
                    // gate `published_notes_count > 0` alone silently
                    // skipped the aggregated fetch for pure-container
                    // parents (e.g. Main Engine with all chapters
                    // under a 4-level-deep descendant) — the middle
                    // pane stayed on "Select a Topic". `descendantNotes`
                    // is already computed above from rollupPublishedNotes.
                    if (topic.published_notes_count > 0 || descendantNotes > 0) {
                      fetchTopicNotes(topic, func.topics);
                    }
                  }}
                >
                  {hasChildren ? (
                    isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )
                  ) : (
                    <div className="w-4" />
                  )}
                  {getTopicIndicator(topic, allTopics)}
                  <div className="flex-1 min-w-0">
                    <span className={`block text-sm min-w-0 line-clamp-2 leading-snug pr-2 break-words ${level > 0 ? 'text-gray-600' : 'font-medium'}`}>
                      {topic.name}
                    </span>
                    {/* Depth hint (iter 105.3): parent has no direct
                        notes but descendants do — nudge the user to expand. */}
                    {hasChildren && own === 0 && descendantNotes > 0 && (
                      <span className="block text-xs text-gray-400 mt-0.5">
                        0 here · {descendantNotes} in sub-topics
                      </span>
                    )}
                  </div>
                  {topic.published_notes_count > 0 && (
                    <Badge variant="outline" className="text-xs flex-shrink-0 self-start mt-0.5">
                      {topic.published_notes_count}
                    </Badge>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                <p>{topic.name}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {hasChildren && isExpanded && (
            <div>
              {renderTopicTree(topic.children.filter(c => topicHasNotes(c)), func, allTopics, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };
  
  // Stats calculation
  const completedCount = Object.values(myProgress).filter(p => p.is_completed).length;
  const bookmarksCount = myBookmarks.size;

  // Check access — matches Progress Tracker's model: paid users must be
  // subscribed to the grade they're viewing. A Class-2 paid user switching
  // preference to Class-4 no longer reads Chief-Mate notes without paying
  // for that grade too.
  const hasGradeAccess =
    !userGrade || subscribedGrade === 'all' || subscribedGrade === userGrade;
  const hasAccess = canAccessNotebook() && hasGradeAccess;

  // Loading state
  if (accessLoading || moduleLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  // No grade selected → prompt to set preferences.
  // Note: MMD isn't required for Notebook content (unlike Q-Bank), so we
  // only gate on grade. Copy reflects that — historical text about
  // "grade and MMD" was misleading.
  if (!userGrade) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto text-center py-16">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">Set Your Grade First</h2>
          <p className="text-gray-500 mb-6">
            Please select your grade in your profile to access study notes.
          </p>
          <Button onClick={() => navigate('/profile')} className="bg-[#14B8A6] hover:bg-[#0F766E]">
            Go to Profile
          </Button>
        </div>
      </Layout>
    );
  }

  if (!isNotebookAvailable || isHidden) {
    // If hidden, redirect to home (module doesn't exist for users)
    if (isHidden) {
      return (
        <Layout>
          <div className="max-w-4xl mx-auto text-center py-16">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-700 mb-2">Page Not Found</h2>
            <p className="text-gray-500 mb-6">
              This feature is not available. Please explore other sections.
            </p>
            <Button onClick={() => navigate('/question-bank')} className="bg-[#14B8A6] hover:bg-[#0F766E]">
              Go to Question Bank
            </Button>
          </div>
        </Layout>
      );
    }
    
    // Coming soon message
    return (
      <Layout>
        <div className="page-transition space-y-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gray-100 rounded-lg">
              <BookOpen className="w-6 h-6 text-gray-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Study Notebook</h1>
              <p className="text-sm text-gray-500">{userGrade}</p>
            </div>
          </div>
          
          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {notebookStatus === 'coming_soon' ? 'Coming Soon' : 'Not Available'}
              </h2>
              <p className="text-gray-600 mb-4">
                {notebookStatus === 'coming_soon' 
                  ? `Study Notebook for ${userGrade} is currently being prepared. We're working hard to bring you comprehensive study materials.`
                  : `Study Notebook is not available for ${userGrade} at this time.`
                }
              </p>
              <p className="text-sm text-gray-500">
                Check back soon or explore other available features.
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate('/question-bank')}
              >
                Explore Question Bank
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Gate content for non-subscribed users
  if (!hasAccess) {
    return (
      <Layout>
        <div className="page-transition space-y-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-[#14B8A6]/10 rounded-lg">
              <BookOpen className="w-6 h-6 text-[#14B8A6]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Study Notebook</h1>
              <p className="text-sm text-gray-500">{userGrade}</p>
            </div>
          </div>
          
          <SubscriptionGate
            hasAccess={false}
            featureName="Study Notebook"
            moduleKey="notebook"
            subscribedGrade={subscribedGrade}
            targetGrade={userGrade}
            description="Access comprehensive study notes, bookmark important topics, track your reading progress, and prepare more effectively with the premium Study Notebook."
          />
        </div>
      </Layout>
    );
  }
  
  // Main two-panel layout
  return (
    <Layout>
      <div className="page-transition h-[calc(100vh-120px)] md:h-[calc(100vh-120px)]">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#14B8A6]/10 rounded-lg">
              <BookOpen className="w-6 h-6 text-[#14B8A6]" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900">Study Notebook</h1>
              <p className="text-sm text-gray-500">{userGrade}</p>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm">
            <div className="flex items-center gap-1 sm:gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span className="font-medium">{completedCount}</span>
              <span className="text-gray-500 hidden sm:inline">completed</span>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <BookMarked className="w-4 h-4 text-[#0A7EA4]" />
              <span className="font-medium">{bookmarksCount}</span>
              <span className="text-gray-500 hidden sm:inline">bookmarks</span>
            </div>
          </div>
        </div>

        {/* Two-Panel Layout - Stack on mobile */}
        <div className="flex flex-col md:flex-row gap-4 h-[calc(100%-80px)] sm:h-[calc(100%-60px)]">
          {/* Left Sidebar - Navigation Tree - Hidden on mobile when viewing chapters or note */}
          <Card className={`${(selectedNote || selectedTopic) ? 'hidden md:flex' : 'flex'} w-full md:w-80 flex-shrink-0 flex-col max-h-[50vh] md:max-h-none overflow-hidden`}>
            <CardHeader className="py-3 px-4 border-b">
              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search notes..."
                  className="pl-9 h-9"
                />
              </div>
              
              {/* Filter Tabs - Changed to All | Completed | Bookmarks */}
              <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setSidebarFilter('all')}
                  className={`flex-1 text-xs py-1.5 px-2 rounded-md transition-colors ${
                    sidebarFilter === 'all' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setSidebarFilter('completed')}
                  className={`flex-1 text-xs py-1.5 px-2 rounded-md transition-colors ${
                    sidebarFilter === 'completed' ? 'bg-white shadow text-green-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Completed
                </button>
                <button
                  onClick={() => setSidebarFilter('bookmarks')}
                  className={`flex-1 text-xs py-1.5 px-2 rounded-md transition-colors ${
                    sidebarFilter === 'bookmarks' ? 'bg-white shadow text-[#0A7EA4]' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Bookmarks
                </button>
              </div>
            </CardHeader>
            
            <ScrollArea className="flex-1">
              <div className="p-2">
                {/* Search Results */}
                {searchResults && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between px-2 mb-2">
                      <span className="text-xs font-medium text-gray-500">
                        Results ({searchResults.total})
                      </span>
                      <button 
                        onClick={() => { setSearchQuery(''); setSearchResults(null); }}
                        className="text-xs text-[#14B8A6]"
                      >
                        Clear
                      </button>
                    </div>
                    {searchResults.results.map((note) => (
                      <div
                        key={note.id}
                        className="p-2 rounded-lg cursor-pointer hover:bg-gray-50 mb-1"
                        onClick={() => {
                          axios.get(`${API}/notes/${note.id}`).then(res => openNote(res.data));
                        }}
                      >
                        <div className="text-sm font-medium text-gray-800 truncate">{note.title}</div>
                        <div className="text-xs text-gray-500 truncate">{note.function_name} → {note.topic_name}</div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Completed Filter View */}
                {sidebarFilter === 'completed' && !searchResults && (
                  <div>
                    {completedNotes.length > 0 ? (
                      completedNotes.map((note) => (
                        <TooltipProvider key={note.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                className={`p-2 rounded-lg cursor-pointer hover:bg-gray-50 mb-1 ${
                                  selectedNote?.id === note.id ? 'bg-green-50 border border-green-200' : ''
                                }`}
                                onClick={() => openNote(note)}
                              >
                                <div className="flex items-center gap-2">
                                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-800 truncate">{note.title}</div>
                                    <div className="text-xs text-gray-400">
                                      Completed {new Date(note.progress?.last_read_at).toLocaleDateString()}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                              <p>{note.title}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        No completed notes yet
                      </div>
                    )}
                  </div>
                )}
                
                {/* Bookmarks Filter View */}
                {sidebarFilter === 'bookmarks' && !searchResults && (
                  <div>
                    {myBookmarksList.length > 0 ? (
                      myBookmarksList.map((bookmark) => (
                        <TooltipProvider key={bookmark.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                className={`p-2 rounded-lg cursor-pointer hover:bg-gray-50 mb-1 group ${
                                  selectedNote?.id === bookmark.note_id ? 'bg-blue-50 border border-blue-200' : ''
                                }`}
                                onClick={() => {
                                  axios.get(`${API}/notes/${bookmark.note_id}`).then(res => openNote(res.data));
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <BookMarked className="w-4 h-4 text-[#0A7EA4] flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-800 truncate">
                                      {bookmark.note_title || 'Saved Note'}
                                    </div>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleBookmark(bookmark.note_id);
                                    }}
                                    className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:bg-red-50 rounded"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                              <p>{bookmark.note_title || 'Saved Note'}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        No bookmarks yet
                      </div>
                    )}
                  </div>
                )}
                
                {/* All Topics Tree View */}
                {sidebarFilter === 'all' && !searchResults && (
                  loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[#14B8A6]" />
                    </div>
                  ) : structure?.functions?.length > 0 ? (
                    <div className="space-y-1">
                      {structure.functions.map((func) => {
                        const hasNotes = func.topics?.some(t => t.published_notes_count > 0);
                        if (!hasNotes) return null;
                        
                        const topicTree = buildTopicTree(func.topics || []);
                        
                        return (
                          <div key={func.id}>
                            {/* Function Header */}
                            <div
                              className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                              onClick={() => toggleFunction(func.id)}
                            >
                              {expandedFunctions[func.id] ? (
                                <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              )}
                              <FolderOpen className="w-4 h-4 text-[#14B8A6] flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-800 truncate">
                                  {func.name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {func.topics?.filter(t => t.published_notes_count > 0).length} topics
                                </div>
                              </div>
                            </div>
                            
                            {/* Topics Tree */}
                            {expandedFunctions[func.id] && topicTree.length > 0 && (
                              <div className="ml-2">
                                {renderTopicTree(
                                  topicTree.filter(t => topicHasNotes(t)), 
                                  func, 
                                  func.topics
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      No notes available for {userGrade}
                    </div>
                  )
                )}
              </div>
            </ScrollArea>
            
            {/* Legend - Progress Indicators */}
            <div className="px-3 py-2 border-t bg-gray-50/50 flex-shrink-0">
              <div className="flex items-center gap-1 mb-1.5">
                <Info className="w-3 h-3 text-gray-400" />
                <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Progress</span>
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-green-500" />
                  <span className="text-[10px] text-gray-600">All done</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3 h-3 text-amber-500" />
                  <span className="text-[10px] text-gray-600">In progress</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Circle className="w-3 h-3 text-[#14B8A6]" />
                  <span className="text-[10px] text-gray-600">Not started</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Circle className="w-3 h-3 text-gray-300" />
                  <span className="text-[10px] text-gray-600">No chapters</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Right Panel - Chapters or Reading View - Full width on mobile, visible when topic or note selected */}
          <Card className={`${(selectedNote || selectedTopic) ? 'flex' : 'hidden md:flex'} flex-1 flex-col overflow-hidden min-h-[50vh] md:min-h-0`}>
            {/* Reading View - Inline */}
            {selectedNote ? (
              <InlineNoteReader
                note={selectedNote}
                onBack={() => setSelectedNote(null)}
                onBookmark={() => toggleBookmark(selectedNote.id)}
                isBookmarked={myBookmarks.has(selectedNote.id)}
                onProgressUpdate={(pos) => updateProgress(selectedNote.id, pos)}
                relatedQuestions={relatedQuestions}
                isCompleted={myProgress[selectedNote.id]?.is_completed}
                onToggleComplete={() => toggleCompletion(selectedNote.id)}
                onFlagNote={flagNote}
                onLoadMoreQuestions={loadMoreQuestions}
                loadingMoreQuestions={loadingMoreQuestions}
                onOpenNote={openNote}
                onOpenTopic={openTopicById}
              />
            ) : selectedTopic && (topicNotes.length > 0 || topicChildGroups.length > 0) ? (
              <>
                {/* Topic Header */}
                <CardHeader className="py-3 px-4 border-b flex-shrink-0">
                  {/* Mobile Back Button */}
                  <button
                    onClick={() => { setSelectedTopic(null); setTopicNotes([]); setTopicChildGroups([]); setTopicInfo(null); }}
                    className="md:hidden flex items-center gap-1 text-sm text-[#0A7EA4] mb-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back to Topics
                  </button>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                    <Home className="w-4 h-4" />
                    <ChevronRight className="w-3 h-3" />
                    <span className="truncate">{topicInfo?.function?.name}</span>
                    <ChevronRight className="w-3 h-3" />
                    <span className="text-gray-700 font-medium truncate">{topicInfo?.topic?.name}</span>
                  </div>
                  <CardTitle className="text-base sm:text-lg">{topicInfo?.topic?.name}</CardTitle>
                  <p className="text-sm text-gray-500">
                    {topicNotes.length + topicChildGroups.reduce((sum, g) => sum + (g.notes?.length || 0), 0)} chapter
                    {(topicNotes.length + topicChildGroups.reduce((sum, g) => sum + (g.notes?.length || 0), 0)) === 1 ? '' : 's'}
                    {topicChildGroups.length > 0 && ` across this topic and ${topicChildGroups.length} subtopic${topicChildGroups.length === 1 ? '' : 's'}`}
                  </p>
                </CardHeader>
                
                <ScrollArea className="flex-1">
                  <div className="p-4 space-y-6">
                    {/* Parent topic's own chapters — only show header when there are also child groups */}
                    {topicNotes.length > 0 && (
                      <div>
                        {topicChildGroups.length > 0 && (
                          <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-gray-100">
                            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{topicInfo?.topic?.name}</span>
                            <span className="text-xs text-gray-400">{topicNotes.length} chapter{topicNotes.length === 1 ? '' : 's'}</span>
                          </div>
                        )}
                        <div className="space-y-2">
                          {topicNotes.map((note, idx) => {
                            const progressStatus = getNoteProgress(note.id);
                            const isCompleted = progressStatus === 'completed';
                            const isInProgress = progressStatus === 'in_progress';
                            return (
                              <div
                                key={note.id}
                                data-testid={`chapter-${note.id}`}
                                className={`flex items-start gap-3 p-3 border rounded-lg hover:shadow-md hover:border-[#14B8A6]/30 transition-all cursor-pointer group ${
                                  isCompleted ? 'border-l-4 border-l-green-500 bg-green-50/30' :
                                  isInProgress ? 'border-l-4 border-l-amber-500 bg-amber-50/30' : ''
                                }`}
                                onClick={() => openNote(note)}
                              >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
                                  isCompleted ? 'bg-green-100 text-green-600' :
                                  isInProgress ? 'bg-amber-100 text-amber-600' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {isCompleted ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start gap-2 mb-1">
                                    <h4 className="font-medium text-gray-900 break-words">{note.title}</h4>
                                    {myBookmarks.has(note.id) && (
                                      <BookmarkCheck className="w-4 h-4 text-[#14B8A6] flex-shrink-0 mt-1" />
                                    )}
                                  </div>
                                  {note.excerpt && (
                                    <p className="text-sm text-gray-500 line-clamp-2">{note.excerpt}</p>
                                  )}
                                  {isInProgress && myProgress[note.id] && (
                                    <div className="flex items-center gap-2 mt-2">
                                      <Progress value={myProgress[note.id].last_position} className="h-1 flex-1 max-w-[100px]" />
                                      <span className="text-xs text-gray-400">{myProgress[note.id].last_position}%</span>
                                    </div>
                                  )}
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Child topic groups — each rendered as its own section with a
                        subheader that scopes the view to that child on click. Keeps
                        the aggregated view browsable but lets users zoom in without
                        going back to the sidebar. */}
                    {topicChildGroups.map((group) => (
                      <div key={group.topic.id}>
                        <button
                          type="button"
                          onClick={() => fetchTopicNotes(group.topic, [])}
                          data-testid={`child-group-header-${group.topic.id}`}
                          className="flex items-center gap-2 mb-2 pb-1.5 border-b border-gray-100 w-full text-left hover:text-[#14B8A6] transition-colors"
                        >
                          <span className="text-xs font-semibold uppercase tracking-wide">{group.topic.name}</span>
                          <span className="text-xs text-gray-400">{group.notes.length} chapter{group.notes.length === 1 ? '' : 's'}</span>
                          <ChevronRight className="w-3 h-3 text-gray-300 ml-auto" />
                        </button>
                        <div className="space-y-2">
                          {group.notes.map((note, idx) => {
                            const progressStatus = getNoteProgress(note.id);
                            const isCompleted = progressStatus === 'completed';
                            const isInProgress = progressStatus === 'in_progress';
                            return (
                              <div
                                key={note.id}
                                data-testid={`chapter-${note.id}`}
                                className={`flex items-start gap-3 p-3 border rounded-lg hover:shadow-md hover:border-[#14B8A6]/30 transition-all cursor-pointer group ${
                                  isCompleted ? 'border-l-4 border-l-green-500 bg-green-50/30' :
                                  isInProgress ? 'border-l-4 border-l-amber-500 bg-amber-50/30' : ''
                                }`}
                                onClick={() => openNote(note)}
                              >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
                                  isCompleted ? 'bg-green-100 text-green-600' :
                                  isInProgress ? 'bg-amber-100 text-amber-600' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {isCompleted ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start gap-2 mb-1">
                                    <h4 className="font-medium text-gray-900 break-words">{note.title}</h4>
                                    {myBookmarks.has(note.id) && (
                                      <BookmarkCheck className="w-4 h-4 text-[#14B8A6] flex-shrink-0 mt-1" />
                                    )}
                                  </div>
                                  {note.excerpt && (
                                    <p className="text-sm text-gray-500 line-clamp-2">{note.excerpt}</p>
                                  )}
                                  {isInProgress && myProgress[note.id] && (
                                    <div className="flex items-center gap-2 mt-2">
                                      <Progress value={myProgress[note.id].last_position} className="h-1 flex-1 max-w-[100px]" />
                                      <span className="text-xs text-gray-400">{myProgress[note.id].last_position}%</span>
                                    </div>
                                  )}
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </>
            ) : selectedTopic && topicNotes.length === 0 && topicChildGroups.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  {/* Mobile Back Button */}
                  <button
                    onClick={() => { setSelectedTopic(null); setTopicNotes([]); setTopicChildGroups([]); setTopicInfo(null); }}
                    className="md:hidden flex items-center gap-1 text-sm text-[#0A7EA4] mb-4 mx-auto"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back to Topics
                  </button>
                  <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-1">No Chapters Yet</h3>
                  <p className="text-sm text-gray-500">
                    This topic doesn&apos;t have any chapters yet
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-1">Select a Topic</h3>
                  <p className="text-sm text-gray-500">
                    Choose a topic from the left panel to view chapters
                  </p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default NotebookPage;
